import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Plus,
  Minus,
  CalendarCheck,
  ShieldCheck,
} from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import CtaBand from "../components/CtaBand";
import {
  findService,
  findServiceAnywhere,
  groupPath,
  servicePath,
} from "../data/services";
import { getServiceContent } from "../data/services/content";
import { OUTCOME_DISCLAIMER } from "../data/legal";
import { WhoDoesWhat } from "./HubPage";
import Placeholder from "./Placeholder";

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-acblue/10 py-4">
      <button
        className="flex w-full items-center justify-between gap-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="font-semibold text-acblue">{q}</span>
        {open ? (
          <Minus className="h-5 w-5 shrink-0 text-acAccent" />
        ) : (
          <Plus className="h-5 w-5 shrink-0 text-acAccent" />
        )}
      </button>
      {open && <p className="mt-3 text-sm text-acBlack/70">{a}</p>}
    </div>
  );
}

export default function ServicePage({ groupSlug, serviceSlug }) {
  const found = findService(groupSlug, serviceSlug);
  if (!found) return <Placeholder />;
  const { group } = found;

  const c = getServiceContent(groupSlug, serviceSlug);
  // No content file yet: render the "coming soon" stub so the page is honest.
  if (!c) return <Placeholder />;

  return (
    <>
      <SEO title={c.title} description={c.intro} path={servicePath(group, found.service)} />
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: group.title, to: groupPath(group) },
          { label: found.service.title },
        ]}
      />

      {/* Hero */}
      <section className="container-page py-12">
        <p className="eyebrow">{c.eyebrow || group.title}</p>
        <h1 className="mt-3 max-w-3xl text-4xl font-bold text-acblue sm:text-5xl">
          {c.title}
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-acBlack/70">{c.intro}</p>

        {/* Summary chips */}
        {c.chips && (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {c.chips.map((chip) => (
              <div
                key={chip.label}
                className="rounded-2xl bg-acNavySoft/50 px-5 py-4"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-acAccent">
                  {chip.label}
                </p>
                <p className="mt-1 font-semibold text-acblue">{chip.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/eligibility-quiz/?service=${c.slug}`} className="btn-primary">
            Check your eligibility
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to={`/book-consultation/?service=${c.slug}`}
            className="btn-ghost"
          >
            Book a consultation
          </Link>
        </div>
      </section>

      <WhoDoesWhat />

      {/* Main two-column body */}
      <section className="bg-acBg py-16">
        <div className="container-page grid gap-12 lg:grid-cols-3">
          <div className="space-y-10 lg:col-span-2">
            {/* Who it's for */}
            {c.whoFor && (
              <div>
                <h2 className="text-2xl font-bold text-acblue">Who it is for</h2>
                <ul className="mt-4 space-y-3">
                  {c.whoFor.map((item, i) => (
                    <li key={i} className="flex gap-3 text-acBlack/80">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-acAccent" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Eligibility essentials */}
            {c.essentials && (
              <div>
                <h2 className="text-2xl font-bold text-acblue">
                  What this pathway involves
                </h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {c.essentials.map((e) => (
                    <div key={e.title} className="card">
                      <h3 className="text-base font-semibold text-acblue">
                        {e.title}
                      </h3>
                      <p className="mt-1 text-sm text-acBlack/70">{e.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Process */}
            {c.process && (
              <div>
                <h2 className="text-2xl font-bold text-acblue">
                  How the process works
                </h2>
                <ol className="mt-6 space-y-5">
                  {c.process.map((step, i) => (
                    <li key={step.title} className="flex gap-4">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-acblue text-sm font-bold text-white">
                        {i + 1}
                      </span>
                      <div>
                        <h3 className="font-semibold text-acblue">
                          {step.title}
                        </h3>
                        <p className="mt-1 text-sm text-acBlack/70">
                          {step.detail}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-4">
              <div className="card">
                <h3 className="text-lg font-semibold text-acblue">
                  Matched with a registered migration agent
                </h3>
                <p className="mt-2 text-sm text-acBlack/70">
                  We get your file application-ready and match you with a
                  registered migration agent for the advice and lodgement, then
                  stay on it the whole way.
                </p>
                <Link
                  to={`/book-consultation/?service=${c.slug}`}
                  className="btn-primary mt-4 w-full"
                >
                  Book a consultation
                </Link>
                <Link
                  to={`/eligibility-quiz/?service=${c.slug}`}
                  className="btn-ghost mt-3 w-full"
                >
                  Free eligibility check
                </Link>
              </div>
              <div className="card">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-acAccent" />
                  <h3 className="text-base font-semibold text-acblue">
                    Application-ready, nothing left to chance
                  </h3>
                </div>
                <p className="mt-2 text-sm text-acBlack/70">
                  Applications are won or lost on the paperwork, and that is our
                  craft. We build a clean, complete, well-evidenced file, close
                  the gaps, and keep everything organised, so the registered
                  migration agent we match you with is handed a case that is
                  ready to go.
                </p>
              </div>
              <div className="flex gap-3 rounded-2xl bg-white p-5 ring-1 ring-acblue/5">
                <CalendarCheck className="h-5 w-5 shrink-0 text-acAccent" />
                <p className="text-xs text-acBlack/60">{OUTCOME_DISCLAIMER}</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQs */}
      {c.faqs && (
        <section className="container-page max-w-3xl py-16">
          <h2 className="text-2xl font-bold text-acblue sm:text-3xl">
            Questions about the {found.service.title.replace(/\(.*\)/, "").trim()}
          </h2>
          <div className="mt-8">
            {c.faqs.map((f) => (
              <FaqItem key={f.q} {...f} />
            ))}
          </div>
        </section>
      )}

      {/* Cross-sell */}
      {c.related && (
        <section className="bg-acBg py-16">
          <div className="container-page">
            <h2 className="text-2xl font-bold text-acblue">Related visas</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {c.related.map((slug) => {
                const r = findServiceAnywhere(slug);
                if (!r) return null;
                return (
                  <Link
                    key={slug}
                    to={servicePath(r.group, r.service)}
                    className="card group flex flex-col transition hover:-translate-y-1 hover:shadow-md"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-acAccent">
                      {r.group.title}
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-acblue">
                      {r.service.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm text-acBlack/70">
                      {r.service.forWho}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-acAccent">
                      Learn more
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <CtaBand service={c.slug} />
    </>
  );
}
